import { useEffect, useState } from 'react';
import type { Tenant } from '../types/Tenant';
import { getTenants } from '../api/tenants';

// Module-level cache (once per session). Cleared on logout via
// clearTenantsCache(). Mirrors useFundTypes / usePages.
let cache: Tenant[] | null = null;
let inflight: Promise<Tenant[]> | null = null;

function loadTenants(): Promise<Tenant[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getTenants()
    .then((d) => {
      cache = d;
      inflight = null;
      return d;
    })
    .catch((e: unknown) => {
      inflight = null;
      throw e;
    });
  return inflight;
}

export function clearTenantsCache(): void {
  cache = null;
  inflight = null;
}

interface UseTenantsResult {
  tenants: Tenant[] | null;
  loading: boolean;
  error: string | null;
}

export function useTenants(): UseTenantsResult {
  const [state, setState] = useState<UseTenantsResult>(() =>
    cache
      ? { tenants: cache, loading: false, error: null }
      : { tenants: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.tenants === cache
          ? prev
          : { tenants: cache, loading: false, error: null },
      );
      return;
    }
    let cancelled = false;
    loadTenants()
      .then((d) => {
        if (!cancelled) setState({ tenants: d, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({
            tenants: null,
            loading: false,
            error: e instanceof Error ? e.message : String(e),
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
